"use client"

import type {
  Column,
  HeaderContext,
  SortingState,
  Table,
} from "@tanstack/react-table"
import { ChevronDown, ChevronUp } from "lucide-react"
import { useTranslations } from "next-intl"

import { cn } from "@/lib/utils"

export function getColumnSortDirection(
  columnId: string,
  sorting: SortingState,
): false | "asc" | "desc" {
  const entry = sorting.find((item) => item.id === columnId)
  if (!entry) {
    return false
  }
  return entry.desc ? "desc" : "asc"
}

function SortDirectionIcon({
  sorted,
}: {
  sorted: false | "asc" | "desc"
}) {
  const upActive = sorted === "asc"
  const downActive = sorted === "desc"
  const isSorted = sorted === "asc" || sorted === "desc"

  return (
    <span
      className="ml-1 inline-flex shrink-0 flex-col leading-none"
      aria-hidden="true"
    >
      <ChevronUp
        className={cn(
          "size-3 stroke-[2.5] transition-colors duration-150",
          upActive
            ? "text-foreground"
            : isSorted
              ? "text-muted-foreground/25"
              : "text-muted-foreground/45",
        )}
      />
      <ChevronDown
        className={cn(
          "-mt-1 size-3 stroke-[2.5] transition-colors duration-150",
          downActive
            ? "text-foreground"
            : isSorted
              ? "text-muted-foreground/25"
              : "text-muted-foreground/45",
        )}
      />
    </span>
  )
}

export function DataTableSortableHeader<TData, TValue>({
  column,
  table,
  title,
  sorted,
  className,
}: {
  column: Column<TData, TValue>
  table: Table<TData>
  title: React.ReactNode
  sorted: false | "asc" | "desc"
  className?: string
}) {
  const t = useTranslations("site.ui")

  const toggleSorting = () => {
    const nextOrder = column.getNextSortingOrder()
    table.setSorting(
      nextOrder === false ? [] : [{ id: column.id, desc: nextOrder === "desc" }],
    )
  }

  return (
    <button
      type="button"
      aria-label={
        typeof title === "string" ? t("sortByColumn", { column: title }) : t("sortThisColumn")
      }
      aria-sort={
        sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"
      }
      className={cn(
        "inline-flex items-center font-medium text-foreground transition-colors hover:text-foreground/80",
        className,
      )}
      onClick={toggleSorting}
    >
      {title}
      <SortDirectionIcon sorted={sorted} />
    </button>
  )
}

export function dataTableSortHeader<TData, TValue>(title: React.ReactNode) {
  function SortHeaderCell({ column, table }: HeaderContext<TData, TValue>) {
    return (
      <DataTableSortableHeader
        column={column}
        table={table}
        title={title}
        sorted={getColumnSortDirection(column.id, table.getState().sorting)}
      />
    )
  }

  return SortHeaderCell
}
